import React, { useEffect, useState, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Checkboxes, SummaryList, Button } from 'nhsuk-react-components';
import { Skeleton } from 'antd';
import { FaShieldAlt, FaExclamationTriangle, FaClock, FaCheckCircle } from 'react-icons/fa';
import { updatePatientConsentIds } from '../../../actions/patientActions';
import { listConsents } from '../../../actions/consentActions';
import Modal from '../../../components/modal/Modal.component';
import Callout from '../../../components/callout/Callout.component';
import { convertNewlinesToBreaks, formatISODate } from '../../../utils/functions';

const ConsentModal = memo(({ show, content, handleClose }) => {
    if (!content) return null;

    const formatValue = (key, value) => {
        if (key.toLowerCase().includes('date') && value) {
            return formatISODate(value, true);
        }
        return typeof value === 'string' ? convertNewlinesToBreaks(value) : (value?.toString() || "-");
    };

    return (
        <Modal show={show} handleClose={handleClose} blur={true} theme="light">
            <div className="nhsuk-u-padding-bottom-4">
                <h2 className="nhsuk-heading-l">{content.consentType}</h2>
                <SummaryList>
                    {Object.entries(content)
                        .filter(([key]) => !["id", "patientConsents"].includes(key))
                        .map(([key, value]) => (
                            <SummaryList.Row key={key}>
                                <SummaryList.Key>
                                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                                </SummaryList.Key>
                                <SummaryList.Value>
                                    {formatValue(key, value)}
                                </SummaryList.Value>
                            </SummaryList.Row>
                        ))}
                </SummaryList>

                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '24px'
                }}>
                    <Button onClick={handleClose}>
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
});

const CONSENT_TYPE_DESCRIPTIONS = {
    "Full Access": "Allows permission to all access types.",
    "Emergency Access": "Permits access to essential health information in emergency situations.",
    "Research Access": "Allows anonymized data to be used for approved clinical or academic research or audit.",
    "Donation Access": "Allows your pacemaker to be voluntarily donated for charitable use if no longer medically required.",
};

const ConsentManagement = memo(({ patient, title = "Manage your consents" }) => {
    const dispatch = useDispatch();
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedConsent, setSelectedConsent] = useState(null);
    const [optimisticConsentIds, setOptimisticConsentIds] = useState(null);
    const [showCallout, setShowCallout] = useState(false);
    const [calloutMessage, setCalloutMessage] = useState({ type: '', message: '' });
    const [showConsentList, setShowConsentList] = useState(false);
    const [showRevokeConfirmation, setShowRevokeConfirmation] = useState(false);
    const [showSuccessIndicator, setShowSuccessIndicator] = useState(false);

    const consentList = useSelector((state) => state.consentList);
    const { consents, loading } = consentList;

    const patientUpdateConsentIds = useSelector((state) => state.patientUpdateConsentIds);
    const { loading: loadingUpdate, error: errorUpdate, success: successUpdate } = patientUpdateConsentIds;

    const currentConsentIds = optimisticConsentIds !== null ? optimisticConsentIds : (patient.consentIds || []);

    useEffect(() => {
        dispatch(listConsents());
    }, [dispatch]);

    useEffect(() => {
        if (patient.consentIds) {
            setOptimisticConsentIds(null);
        }
    }, [patient.consentIds]);

    useEffect(() => {
        if (successUpdate) {
            setOptimisticConsentIds(null);
            setShowCallout(false);
            setShowSuccessIndicator(true);
        } else if (errorUpdate) {
            setOptimisticConsentIds(null);
            setShowSuccessIndicator(false);
            setCalloutMessage({
                type: 'error',
                message: 'Failed to update consent. Please try again.'
            });
            setShowCallout(true);
        }
    }, [successUpdate, errorUpdate]);

    useEffect(() => {
        if (showSuccessIndicator) {
            const timer = setTimeout(() => {
                setShowSuccessIndicator(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showSuccessIndicator]);

    useEffect(() => {
        if (showCallout && calloutMessage.type === 'error') {
            const timer = setTimeout(() => {
                setShowCallout(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showCallout, calloutMessage.type]);

    const handlePrimaryConsentChange = useCallback((checked) => {
        if (!checked) {
            setShowRevokeConfirmation(true);
        } else {
            setShowConsentList(true);
        }
    }, []);

    const handleRevokeConfirmation = useCallback(() => {
        setOptimisticConsentIds([]);
        setShowRevokeConfirmation(false);
        setShowConsentList(false);

        dispatch(updatePatientConsentIds(patient.id, {
            ...patient,
            consentIds: []
        }));
    }, [patient, dispatch]);

    useEffect(() => {
        if (patient.isConsentingPatient) {
            setShowConsentList(true);
        }
    }, [patient.isConsentingPatient]);

    const handleConsentChange = useCallback((consent, checked) => {
        let updatedConsents;

        if (consent.consentType === "Full Access") {
            updatedConsents = checked ? consents.map(c => c.id) : [];
        } else {
            if (checked) {
                updatedConsents = [...currentConsentIds, consent.id];
            } else {
                updatedConsents = currentConsentIds.filter(id => id !== consent.id);
                const fullAccessId = consents.find(c => c.consentType === "Full Access")?.id;
                if (fullAccessId && currentConsentIds.includes(fullAccessId)) {
                    updatedConsents = updatedConsents.filter(id => id !== fullAccessId);
                }
            }
        }

        setOptimisticConsentIds(updatedConsents);

        dispatch(updatePatientConsentIds(patient.id, {
            ...patient,
            consentIds: updatedConsents
        }));
    }, [patient, consents, dispatch, currentConsentIds]);

    const handleViewConsentDetails = useCallback((consent) => {
        setSelectedConsent(consent);
        setModalVisible(true);
    }, []);

    return (
        <>
            <Card className="nhsuk-u-margin-bottom-4" style={{
                background: "#f0f7ff",
                border: "1px solid #005eb8",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,94,184,0.1)"
            }}>
                <Card.Content>
                    <div style={{
                        borderBottom: "1px solid #005eb8",
                        marginBottom: "24px",
                        paddingBottom: "16px"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "16px"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                flex: 1
                            }}>
                                <div style={{
                                    background: "#005eb8",
                                    borderRadius: "50%",
                                    width: "40px",
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 2px 4px rgba(0,94,184,0.2)"
                                }}>
                                    <FaShieldAlt size={20} style={{ color: "white" }} />
                                </div>
                                <div>
                                    <h2 className="nhsuk-heading-m" style={{
                                        margin: 0,
                                        color: "#212b32",
                                        fontSize: "24px",
                                        fontWeight: 600,
                                        lineHeight: 1.2
                                    }}>
                                        {title}
                                    </h2>
                                    <p style={{
                                        margin: "4px 0 0 0",
                                        color: "#4c6272",
                                        fontSize: "14px"
                                    }}>
                                        Control access to patient medical information
                                    </p>
                                </div>
                            </div>
                            {loadingUpdate && (
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    color: "#005eb8",
                                    fontSize: "16px",
                                    fontWeight: 500
                                }}>
                                    <span style={{ fontSize: "14px" }}>Saving...</span>
                                </div>
                            )}
                            {showSuccessIndicator && !loadingUpdate && (
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    color: "#007f3b",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    opacity: 1,
                                    transition: "opacity 0.3s ease-in"
                                }}>
                                    <FaCheckCircle size={20} />
                                    <span style={{ fontSize: "14px" }}>Saved</span>
                                </div>
                            )}
                        </div>
                        {patient?.dateConsented && (
                            <div style={{
                                marginLeft: '50px',
                                marginTop: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#4c6272',
                                fontSize: '14px'
                            }}>
                                <FaClock size={14} />
                                <span>Consent updated on {formatISODate(patient.dateConsented, true)}</span>
                            </div>
                        )}
                    </div>

                    <Card.Description>
                        <div className="nhsuk-u-padding-bottom-3">
                            {showCallout && (
                                <div className="nhsuk-u-margin-bottom-4">
                                    <Callout
                                        label={calloutMessage.type === 'success' ? 'Success' : 'Error'}
                                        header={calloutMessage.type === 'success' ? 'Success!' : 'Error'}
                                        autoDismiss={true}
                                        dismissTime={5000}
                                        time={formatISODate(new Date(), true)}
                                        message={calloutMessage.message}
                                        mode={calloutMessage.type}
                                    />
                                </div>
                            )}

                            <div className="nhsuk-u-margin-bottom-4">
                                <Checkboxes.Box
                                    key={`primary-consent-${patient.id}`}
                                    checked={patient.isConsentingPatient || showConsentList}
                                    onChange={(e) => handlePrimaryConsentChange(e.target.checked)}
                                >
                                    I consent to authorised NHS healthcare professionals accessing my implanted device information for:
                                </Checkboxes.Box>
                            </div>

                            {showConsentList && (
                                <div className="nhsuk-u-margin-left-4">
                                    {loading ? (
                                        [...Array(3)].map((_, i) => (
                                            <Skeleton
                                                key={i}
                                                active
                                                paragraph={{ rows: 1 }}
                                                style={{ marginBottom: "12px" }}
                                            />
                                        ))
                                    ) : !consents?.length ? (
                                        <div className="nhsuk-inset-text">
                                            <p>No additional consents available</p>
                                        </div>
                                    ) : (
                                        <>
                                            <Checkboxes>
                                                {consents
                                                    .sort((a, b) => a.consentType === "Full Access" ? -1 : b.consentType === "Full Access" ? 1 : 0)
                                                    .map(consent => (
                                                        <Checkboxes.Box
                                                            key={consent.id}
                                                            value={consent.id}
                                                            checked={currentConsentIds.includes(consent.id)}
                                                            onChange={(e) => handleConsentChange(consent, e.target.checked)}
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <div>{consent.consentType}</div>
                                                                    {CONSENT_TYPE_DESCRIPTIONS[consent.consentType] && (
                                                                        <div style={{
                                                                            fontSize: "14px",
                                                                            color: "#4c6272",
                                                                            marginTop: "4px"
                                                                        }}>
                                                                            {CONSENT_TYPE_DESCRIPTIONS[consent.consentType]}
                                                                        </div>
                                                                    )}
                                                                    {consent.validFromDate && (
                                                                        <div style={{
                                                                            fontSize: "14px",
                                                                            color: "#4c6272",
                                                                            marginTop: "4px"
                                                                        }}>
                                                                            Valid from: {formatISODate(consent.validFromDate, true)}
                                                                        </div>
                                                                    )}
                                                                    {consent.validToDate && (
                                                                        <div style={{
                                                                            fontSize: "14px",
                                                                            color: "#4c6272",
                                                                            marginTop: "4px"
                                                                        }}>
                                                                            Valid until: {formatISODate(consent.validToDate, true)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handleViewConsentDetails(consent);
                                                                    }}
                                                                    style={{
                                                                        color: "#005eb8",
                                                                        textDecoration: "underline",
                                                                        fontSize: "14px",
                                                                        marginLeft: "12px",
                                                                        whiteSpace: "nowrap"
                                                                    }}
                                                                >
                                                                    View details
                                                                </a>
                                                            </div>
                                                        </Checkboxes.Box>
                                                    ))
                                                }
                                            </Checkboxes>
                                            <p style={{ marginTop: "16px", fontSize: "14px", color: "#4c6272" }}>
                                                <a 
                                                    href="/consent-information" 
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: "#005eb8", textDecoration: "underline" }}
                                                >
                                                    Learn more
                                                </a>
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </Card.Description>
                </Card.Content>
            </Card>
            <ConsentModal
                show={modalVisible}
                content={selectedConsent}
                handleClose={() => {
                    setModalVisible(false);
                    setSelectedConsent(null);
                }}
            />

            <Modal
                show={showRevokeConfirmation}
                handleClose={() => setShowRevokeConfirmation(false)}
                blur={true}
                theme="light"
            >
                <div className="nhsuk-u-padding-bottom-4">
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '24px',
                        color: '#d5281b'
                    }}>
                        <FaExclamationTriangle size={24} />
                        <h2 className="nhsuk-heading-l" style={{ margin: 0, color: '#d5281b' }}>
                            Revoke Consent
                        </h2>
                    </div>
                    <p className="nhsuk-body">
                        Are you sure you want to revoke your consent for authorised NHS healthcare professionals to access your implanted device information?
                        This action will restrict access to your medical information.
                    </p>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        marginTop: '24px'
                    }}>
                        <Button secondary onClick={() => setShowRevokeConfirmation(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleRevokeConfirmation}
                            style={{ backgroundColor: '#d5281b', color: 'white' }}
                        >
                            Revoke Consent
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
   );
});

export default ConsentManagement;